import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Check, Target, MapPin, Calendar, Home, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const OnboardingFlow = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const totalSteps = 5;
  const [startTime, setStartTime] = useState(Date.now());
  const [stepStartTime, setStepStartTime] = useState(Date.now());
  
  const [onboardingData, setOnboardingData] = useState({
    // Step 1: Goals
    goals: [],
    
    // Step 2: Situation
    situation: '',
    
    // Step 3: Location & Timeline
    province: '',
    renewalDeadline: '',
    
    // Step 4: Mortgage Details
    lender: '',
    initialBalance: '',
    amortization: 25,
    amortizationRemain: 20,
    currentBalance: '',
    currentRate: '',
    homeValue: '',
    renewalDate: '',
    paymentFrequency: 'monthly',
    propertyTaxes: '',
    jobLossInsurance: '',
    primaryResidence: 'yes',
    
    // Step 5: Offers (optional)
    offers: []
  });
  
  const [newOffer, setNewOffer] = useState({
    lender: '',
    rate: '',
    term: '5',
    type: 'fixed',
    frequency: 'monthly'
  });
  
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Load saved progress on mount
  useEffect(() => {
    loadSavedProgress();
  }, []);
  
  // Track step changes
  useEffect(() => {
    setStepStartTime(Date.now());
  }, [step]);
  
  const loadSavedProgress = async () => {
    try {
      const response = await api.get('/api/onboarding/progress');
      
      if (response.data && response.data.data) {
        // Resume from saved progress
        setOnboardingData(response.data.data);
        setStep(response.data.current_step);
        
        // Track resume event
        await trackEvent('resumed', response.data.current_step);
        
        toast.success('Welcome back! Resuming your setup...');
      } else {
        // New onboarding - track start
        await trackEvent('started', 1);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
      // Track start for new users
      await trackEvent('started', 1);
    } finally {
      setLoading(false);
    }
  };
  
  const trackEvent = async (eventType, stepNumber = null, metadata = null) => {
    try {
      const timeSpent = stepNumber ? (Date.now() - stepStartTime) / 1000 : null;
      
      await api.post('/api/onboarding/analytics', {
        event_type: eventType,
        step_number: stepNumber,
        step_name: getStepName(stepNumber),
        time_spent: timeSpent,
        metadata: metadata
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  };
  
  const getStepName = (stepNum) => {
    const names = {
      1: 'goals',
      2: 'situation',
      3: 'location_timeline',
      4: 'mortgage_details',
      5: 'offers'
    };
    return names[stepNum] || 'unknown';
  };
  
  const saveProgress = async (showToast = true) => {
    setSaving(true);
    try {
      await api.post('/api/onboarding/progress', {
        current_step: step,
        data: onboardingData
      });
      
      if (showToast) {
        toast.success('Progress saved! You can continue later.');
      }
    } catch (error) {
      console.error('Error saving progress:', error);
      if (showToast) {
        toast.error('Failed to save progress');
      }
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveAndExit = async () => {
    await saveProgress(true);
    await trackEvent('abandoned', step, { reason: 'save_and_exit' });
    
    // Exit to dashboard
    setTimeout(() => {
      onComplete();
    }, 1000);
  };
  
  const goalOptions = [
    { id: 'payoff', label: 'Pay off mortgage early', icon: '🏠' },
    { id: 'reduce_interest', label: 'Minimize interest payments', icon: '💰' },
    { id: 'minimize_payment', label: 'Minimize monthly payments', icon: '📉' },
    { id: 'refinance', label: 'Get better rate', icon: '🔄' },
    { id: 'heloc', label: 'Access home equity (HELOC)', icon: '🏦' }
  ];
  
  const situationOptions = [
    { id: 'transfer', label: 'Transfer to another lender', description: 'Move to a new lender with better rates' },
    { id: 'negotiate', label: 'Negotiate with current lender', description: 'Stay but get a better rate' },
    { id: 'refinance', label: 'Refinance to increase loan', description: 'Access equity and get better tiered rates' },
    { id: 'renew', label: 'Simple renewal', description: 'Renew with current lender as-is' }
  ];
  
  const provinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 
    'Newfoundland and Labrador', 'Nova Scotia', 'Ontario', 
    'Prince Edward Island', 'Quebec', 'Saskatchewan'
  ];
  
  const toggleGoal = (goalId) => {
    setOnboardingData(prev => ({
      ...prev,
      goals: prev.goals.includes(goalId)
        ? prev.goals.filter(g => g !== goalId)
        : [...prev.goals, goalId]
    }));
  };
  
  const addOffer = () => {
    if (newOffer.lender && newOffer.rate && newOffer.term) {
      setOnboardingData(prev => ({
        ...prev,
        offers: [...prev.offers, { ...newOffer, rate: parseFloat(newOffer.rate), term: parseInt(newOffer.term) }]
      }));
      setNewOffer({ lender: '', rate: '', term: '5', type: 'fixed', frequency: 'monthly' });
      toast.success('Offer added');
    }
  };
  
  const removeOffer = (index) => {
    setOnboardingData(prev => ({
      ...prev,
      offers: prev.offers.filter((_, i) => i !== index)
    }));
  };
  
  const canProceed = () => {
    switch(step) {
      case 1:
        return onboardingData.goals.length > 0;
      case 2:
        return onboardingData.situation !== '';
      case 3:
        return onboardingData.province !== '' && onboardingData.renewalDeadline !== '';
      case 4:
        return onboardingData.currentBalance !== '' && onboardingData.currentRate !== '';
      case 5:
        return true; // Offers are optional
      default:
        return false;
    }
  };
  
  const handleNext = () => {
    if (canProceed()) {
      // Track step completion
      trackEvent('step_completed', step, { 
        goals_count: step === 1 ? onboardingData.goals.length : undefined
      });
      
      // Auto-save progress
      saveProgress(false);
      
      setStep(step + 1);
    }
  };
  
  const handleBack = () => {
    setStep(step - 1);
  };
  
  const handleComplete = async () => {
    try {
      // Track completion
      await trackEvent('completed', 5);
      
      // Save mortgage details
      const mortgageData = {
        lender: onboardingData.lender,
        initial_balance: parseFloat(onboardingData.initialBalance) || parseFloat(onboardingData.currentBalance) || 0,
        amortization: parseInt(onboardingData.amortization) || 25,
        amortization_remain: parseInt(onboardingData.amortizationRemain) || 20,
        current_balance: parseFloat(onboardingData.currentBalance) || 0,
        current_rate: parseFloat(onboardingData.currentRate) || 0,
        home_value: parseFloat(onboardingData.homeValue) || 0,
        renewal_date: onboardingData.renewalDate || onboardingData.renewalDeadline,
        payment_frequency: onboardingData.paymentFrequency,
        property_taxes: parseFloat(onboardingData.propertyTaxes) || 0,
        job_loss_insurance: parseFloat(onboardingData.jobLossInsurance) || 0,
        primary_residence: onboardingData.primaryResidence,
        province: onboardingData.province,
        situation: onboardingData.situation,
        goals: onboardingData.goals.join(',')
      };
      
      await api.post('/api/mortgage', mortgageData);
      
      // Save offers if any
      for (const offer of onboardingData.offers) {
        await api.post('/api/offers', offer);
      }
      
      // Mark onboarding as complete in backend
      await api.post('/api/onboarding/complete', {});
      
      // Mark onboarding as complete in localStorage
      localStorage.setItem('onboarding_completed', 'true');
      
      toast.success('Welcome to My Mortgage Canada!');
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to save your information. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-3xl w-full">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-heading font-semibold text-foreground">Let's get you started</h2>
            <span className="text-sm text-muted-foreground">Step {step} of {totalSteps}</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-8">
          {/* Step 1: Goals */}
          {step === 1 && (
            <div data-testid="onboarding-step-goals" className="space-y-6">
              <div className="text-center mb-6">
                <Target className="h-12 w-12 text-primary mx-auto mb-3" />
                <h3 className="text-2xl font-heading font-semibold mb-2">What are your mortgage goals?</h3>
                <p className="text-muted-foreground">Select all that apply</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {goalOptions.map(goal => (
                  <button
                    key={goal.id}
                    data-testid={`goal-${goal.id}`}
                    onClick={() => toggleGoal(goal.id)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      onboardingData.goals.includes(goal.id)
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{goal.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium">{goal.label}</p>
                      </div>
                      {onboardingData.goals.includes(goal.id) && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Step 2: Situation */}
          {step === 2 && (
            <div data-testid="onboarding-step-situation" className="space-y-6">
              <div className="text-center mb-6">
                <Home className="h-12 w-12 text-primary mx-auto mb-3" />
                <h3 className="text-2xl font-heading font-semibold mb-2">What's your situation?</h3>
                <p className="text-muted-foreground">Choose the option that best describes your plan</p>
              </div>
              
              <div className="space-y-3">
                {situationOptions.map(option => (
                  <button
                    key={option.id}
                    data-testid={`situation-${option.id}`}
                    onClick={() => setOnboardingData(prev => ({ ...prev, situation: option.id }))}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      onboardingData.situation === option.id
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium mb-1">{option.label}</p>
                        <p className="text-sm text-muted-foreground">{option.description}</p>
                      </div>
                      {onboardingData.situation === option.id && (
                        <Check className="h-5 w-5 text-primary flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Step 3: Location & Timeline */}
          {step === 3 && (
            <div data-testid="onboarding-step-location" className="space-y-6">
              <div className="text-center mb-6">
                <MapPin className="h-12 w-12 text-primary mx-auto mb-3" />
                <h3 className="text-2xl font-heading font-semibold mb-2">Where and when?</h3>
                <p className="text-muted-foreground">Tell us about your location and timeline</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Province</label>
                  <select
                    data-testid="province-select"
                    value={onboardingData.province}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, province: e.target.value }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select your province</option>
                    {provinces.map(province => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Mortgage Renewal Deadline</label>
                  <input
                    type="date"
                    data-testid="renewal-deadline-input"
                    value={onboardingData.renewalDeadline}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, renewalDeadline: e.target.value, renewalDate: e.target.value }))}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Step 4: Mortgage Details */}
          {step === 4 && (
            <div data-testid="onboarding-step-mortgage" className="space-y-6">
              <div className="text-center mb-6">
                <Home className="h-12 w-12 text-primary mx-auto mb-3" />
                <h3 className="text-2xl font-heading font-semibold mb-2">Your mortgage details</h3>
                <p className="text-muted-foreground">Help us understand your current mortgage</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Current Lender</label>
                  <input
                    type="text"
                    value={onboardingData.lender}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, lender: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="e.g., TD Bank"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">Current Balance (CAD $) *</label>
                  <input
                    type="number"
                    data-testid="current-balance-input"
                    value={onboardingData.currentBalance}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, currentBalance: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="400000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">Current Rate (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    data-testid="current-rate-input"
                    value={onboardingData.currentRate}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, currentRate: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="4.99"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">Home Value (CAD $)</label>
                  <input
                    type="number"
                    value={onboardingData.homeValue}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, homeValue: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="600000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">Years Remaining</label>
                  <input
                    type="number"
                    value={onboardingData.amortizationRemain}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, amortizationRemain: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                    placeholder="20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5">Payment Frequency</label>
                  <select
                    value={onboardingData.paymentFrequency}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, paymentFrequency: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 5: Offers */}
          {step === 5 && (
            <div data-testid="onboarding-step-offers" className="space-y-6">
              <div className="text-center mb-6">
                <Sparkles className="h-12 w-12 text-primary mx-auto mb-3" />
                <h3 className="text-2xl font-heading font-semibold mb-2">Any offers yet?</h3>
                <p className="text-muted-foreground">Add any mortgage offers you've received (optional)</p>
              </div>
              
              {/* Existing offers */}
              {onboardingData.offers.length > 0 && (
                <div className="space-y-2 mb-4">
                  {onboardingData.offers.map((offer, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-background rounded-lg">
                      <div>
                        <p className="font-medium">{offer.lender}</p>
                        <p className="text-sm text-muted-foreground">
                          {offer.rate}% • {offer.term}-year {offer.type}
                        </p>
                      </div>
                      <button
                        onClick={() => removeOffer(idx)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new offer */}
              <div className="border border-border rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      placeholder="Lender name"
                      value={newOffer.lender}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, lender: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Rate %"
                      value={newOffer.rate}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, rate: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                    />
                  </div>
                  <div>
                    <select
                      value={newOffer.term}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, term: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                    >
                      <option value="1">1 Year</option>
                      <option value="2">2 Years</option>
                      <option value="3">3 Years</option>
                      <option value="5">5 Years</option>
                    </select>
                  </div>
                  <div>
                    <select
                      value={newOffer.type}
                      onChange={(e) => setNewOffer(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                    >
                      <option value="fixed">Fixed</option>
                      <option value="variable">Variable</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={addOffer}
                  data-testid="add-offer-btn"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/10 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Offer
                </button>
              </div>
              
              <p className="text-xs text-center text-muted-foreground">
                You can always add more offers later from your dashboard
              </p>
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <div className="flex gap-3">
              <button
                onClick={handleBack}
                disabled={step === 1}
                className="flex items-center gap-2 px-4 py-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
              
              <button
                onClick={handleSaveAndExit}
                disabled={saving}
                data-testid="save-and-exit-btn"
                className="flex items-center gap-2 px-4 py-2 border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg"
              >
                {saving ? 'Saving...' : 'Save & Continue Later'}
              </button>
            </div>
            
            {step < totalSteps ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                data-testid="next-step-btn"
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                data-testid="complete-onboarding-btn"
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Check className="h-4 w-4" />
                Complete Setup
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFlow;